import React from 'react';
import {
  Handle,
  Position,
  NodeResizer,
  useNodeId,
  useReactFlow,
  type Node as RFNode,
  type NodeProps,
} from '@xyflow/react';


// Use relative base so dev+prod "just work"
const API_BASE = '/api';

type FetchOpts = RequestInit & { json?: unknown };

// CSRF imports
import Cookies from 'universal-cookie';
const cookies = new Cookies();

async function apiFetch<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers = new Headers(opts.headers);

  // Always send/expect JSON
  if (!headers.has('Content-Type') && opts.method && opts.method !== 'GET') {
    headers.set('Content-Type', 'application/json');
  }

  // CSRF for Django when same-origin (works via Vite proxy too)
  const getCsrfToken = (): string | null => {
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    return metaTag ? metaTag.getAttribute('content') : null; // Return null if the meta tag is not found
  };
  let csrfToken = cookies.get('csrftoken'); // Get the CSRF token from cookies

  if (!csrfToken) {
    csrfToken = getCsrfToken();
    if (!csrfToken) {
      console.error('CSRF token not found');
    }
  }
  if (csrfToken && !headers.has('X-CSRFToken')) {
    headers.set('X-CSRFToken', decodeURIComponent(csrfToken));
  }

  const res = await fetch(url, {
    credentials: 'include', // send cookies/session
    ...opts,
    headers,
    body: opts.json !== undefined ? JSON.stringify(opts.json) : opts.body,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status} ${res.statusText}: ${text}`);
  }

  // Handle empty 204/empty body
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) return undefined as unknown as T;
  return (await res.json()) as T;
}

// ===== Types =====
export type ApiNode = {
  id: string;
  type?: string;
  position: { x: number; y: number };
  data: Record<string, any>;
};
export type ApiEdge = {
  id: string;
  source: string;
  target: string;
  label?: string;
  data?: Record<string, any>;
};
export interface GraphData {
  nodes: ApiNode[];
  edges: ApiEdge[];
  name: string; // Expect a string for the name
  description: string; // Expect a string for the description
}
// ===== API calls =====
export async function fetchGraph(flowchartId: string) {
  return apiFetch<{ nodes: ApiNode[]; edges: ApiEdge[]; name: '', description: ''}>(
    `/flowcharts/${flowchartId}/graph/`
  );
}

export async function saveGraph(
  flowchartId: string,
  name: string,
  description: string,
  payload: { nodes: ApiNode[]; edges: ApiEdge[]; replace?: boolean }
) {
  // Combine the payload with the name and description
  const dataToSend = {
    name,
    description,
    ...payload, // Spread the nodes and edges into this object
  };

  await apiFetch<void>(`/flowcharts/${flowchartId}/graph/`, {
    method: 'POST',
    json: dataToSend, // Send the combined data
  });
}

export type Flowchart = {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at: string;
  created_by_username: string;
};

export async function listFlowcharts(): Promise<Flowchart[]> {
  const data = await apiFetch<any>('/flowcharts/');
  // If DRF pagination is enabled, `data.results` exists.
  return Array.isArray(data) ? data : data.results ?? [];
}

export async function createFlowchart(): Promise<Flowchart> {
  // backend probably needs just name/description
  return apiFetch<Flowchart>('/flowcharts/', {
    method: 'POST',
    json: { name: 'Untitled Flow', description: '' },
  });
}

export async function duplicateFlowchart(id: string): Promise<Flowchart> {
  return apiFetch<Flowchart>(`/flowcharts/${id}/duplicate/`, {
    method: 'POST',
  });
}

export async function deleteFlowchart(id: string): Promise<void> {
  await apiFetch<void>(`/flowcharts/${id}/`, { method: 'DELETE' });
}

// CustomNode component
type CustomNodeData = {
  label: string;
  description?: string;
  width?: number;
  height?: number;
};

